import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"

const DEMO_STORAGE_KEY = "demo_contact_groups"

// Toggle-chip group editor: every group renders as a chip; tapping a chip
// adds/removes the contact from that group. The trailing "New group" chip
// swaps to an inline input for creating (and assigning) a custom group.
export default class extends Controller {
    static values = {
        contactId: String,
        demo: Boolean,
        memberSlugs: Array,
        membershipBase: String,
        createUrl: String
    }

    static targets = ["chips", "chip", "newChip", "input", "addBtn"]

    connect() {
        if (this.demoValue) this._mergeDemoState()
        this._syncMembership()
    }

    toggleAdd(e) {
        e.preventDefault()
        if (this.element.classList.contains("is-expanded")) this.collapseAdd()
        else this.expandAdd()
    }

    expandAdd() {
        this.element.classList.add("is-expanded")
        if (this.hasAddBtnTarget) this.addBtnTarget.textContent = "Done"
    }

    collapseAdd() {
        this.element.classList.remove("is-expanded")
        if (this.hasAddBtnTarget) this.addBtnTarget.textContent = "+ Add"
        this.hideCreate()
    }

    toggle(e) {
        let slug = e.currentTarget.dataset.slug
        if (!slug) return
        if (this._isMember(slug)) this._unassign(slug)
        else this._assign(slug)
    }

    startCreate() {
        this.newChipTarget.hidden = true
        this.inputTarget.hidden = false
        this.inputTarget.focus()
    }

    hideCreate() {
        this.inputTarget.value = ""
        this.inputTarget.hidden = true
        this.newChipTarget.hidden = false
    }

    inputKeydown(e) {
        if (e.key === "Enter") {
            e.preventDefault()
            this.createGroup()
        } else if (e.key === "Escape") {
            // Stop it from reaching detail-modal's document listener, which
            // would close the whole panel.
            e.preventDefault()
            e.stopPropagation()
            this.hideCreate()
        }
    }

    createGroup() {
        let name = this.inputTarget.value.trim()
        if (!name) {
            this.hideCreate()
            return
        }

        let existing = this.chipTargets.find((chip) => chip.dataset.name.toLowerCase() === name.toLowerCase())
        if (existing) {
            if (!this._isMember(existing.dataset.slug)) this._assign(existing.dataset.slug)
            this.hideCreate()
            return
        }

        if (this.demoValue) {
            let group = this._createDemoGroup(name)
            this._appendChip(group.slug, group.name)
            this._setDemoMember(group.slug, true)
            this.hideCreate()
            return
        }

        fetch(this.createUrlValue, {
            method: "POST",
            headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin",
            body: JSON.stringify({ name })
        })
            .then((response) => response.json().then((data) => ({ response, data })))
            .then(({ response, data }) => {
                if (!response.ok || !data.ok) throw new Error(data.error || "Could not create group")
                this._appendChip(data.group.slug, data.group.name)
                this.hideCreate()
                this._assign(data.group.slug)
            })
            .catch(() => {})
    }

    _assign(slug) {
        if (this.demoValue) {
            this._setDemoMember(slug, true)
            return
        }
        this._requestMembership(slug, "POST")
    }

    _unassign(slug) {
        if (this.demoValue) {
            this._setDemoMember(slug, false)
            return
        }
        this._requestMembership(slug, "DELETE")
    }

    _requestMembership(slug, method) {
        let url = `${this.membershipBaseValue}${encodeURIComponent(slug)}`
        fetch(url, {
            method,
            headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin"
        })
            .then((response) => response.json().then((data) => ({ response, data })))
            .then(({ response, data }) => {
                if (!response.ok || !data.ok) throw new Error(data.error || "Could not update group")
                this._setMember(slug, data.member)
            })
            .catch(() => {})
    }

    _setMember(slug, member) {
        let slugs = new Set(this.memberSlugsValue)
        if (member) slugs.add(slug)
        else slugs.delete(slug)
        this.memberSlugsValue = [...slugs]
        this._syncMembership()
    }

    _syncMembership() {
        let slugs = new Set(this.memberSlugsValue)
        this.chipTargets.forEach((chip) => {
            let active = slugs.has(chip.dataset.slug)
            chip.classList.toggle("is-active", active)
            chip.setAttribute("aria-pressed", active ? "true" : "false")
        })
    }

    _isMember(slug) {
        return this.memberSlugsValue.includes(slug)
    }

    _appendChip(slug, name) {
        if (this.chipTargets.some((chip) => chip.dataset.slug === slug)) return

        let chip = document.createElement("button")
        chip.type = "button"
        chip.className = "contact-chip contact-group-chip"
        chip.setAttribute("aria-pressed", "false")
        chip.dataset.contactGroupsTarget = "chip"
        chip.dataset.slug = slug
        chip.dataset.name = name
        chip.dataset.action = "contact-groups#toggle"
        chip.innerHTML = `<i class="bi contact-group-chip-icon bi-tag"></i><span class="contact-group-chip-label">${this._escapeHtml(name)}</span><i class="bi bi-plus-lg contact-group-chip-plus" aria-hidden="true"></i><i class="bi bi-check-lg contact-group-chip-check" aria-hidden="true"></i>`
        this.chipsTarget.insertBefore(chip, this.hasNewChipTarget ? this.newChipTarget : (this.hasAddBtnTarget ? this.addBtnTarget : null))
    }

    _mergeDemoState() {
        let state = this._loadDemoState()
        let slugs = new Set(this.memberSlugsValue)
        let stored = state.memberships[this.contactIdValue]
        if (stored) stored.forEach((slug) => slugs.add(slug))
        this.memberSlugsValue = [...slugs]

        state.custom_groups.forEach((group) => {
            this._appendChip(group.slug, group.name)
        })
    }

    _setDemoMember(slug, member) {
        let state = this._loadDemoState()
        let slugs = new Set(state.memberships[this.contactIdValue] || [])
        if (member) slugs.add(slug)
        else slugs.delete(slug)
        state.memberships[this.contactIdValue] = [...slugs]
        this._saveDemoState(state)
        this._setMember(slug, member)
        document.dispatchEvent(new CustomEvent("demo-contact-groups:changed", { detail: { contactId: this.contactIdValue, slugs: [...slugs] } }))
    }

    _createDemoGroup(name) {
        let state = this._loadDemoState()
        let base = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "group"
        let slug = base
        let suffix = 2
        let known = new Set([...state.custom_groups.map((g) => g.slug), ...this.chipTargets.map((chip) => chip.dataset.slug)])
        while (known.has(slug)) {
            slug = `${base}-${suffix}`
            suffix += 1
        }
        let group = { name, slug }
        state.custom_groups.push(group)
        this._saveDemoState(state)
        return group
    }

    _loadDemoState() {
        try {
            let raw = sessionStorage.getItem(DEMO_STORAGE_KEY)
            let parsed = raw ? JSON.parse(raw) : {}
            return {
                memberships: parsed.memberships || {},
                custom_groups: parsed.custom_groups || []
            }
        } catch {
            return { memberships: {}, custom_groups: [] }
        }
    }

    _saveDemoState(state) {
        try {
            sessionStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(state))
        } catch {
            // sessionStorage unavailable — ignore
        }
    }

    _escapeHtml(text) {
        return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
    }
}
